// Core lib dependencies.
import React from 'react';
import PropTypes from 'prop-types';

import _ from 'lodash';

import { capitalize, UCFirst } from '../../../common/utils/string.js';

// Model objects.
import DisplayItem from '../../../common/model/entity/DisplayItem/DisplayItem.js';

import ItemUtils from '../utils/item-utils';
import ModuleUtils from '../utils/module-utils';

import Custom from '../../../common/components/displayItems/items/Custom.jsx';

import itemFactory from './ItemFactory';
/**
 * Namespace for UgcWall element components.
 *
 * @memberof module:UgcWall.Components
 * @namespace Elements
 */

/**
 * <pre>
 * UgcWall > Items React component.
 *
 * Summary of responsibilities:
 *
 *  - dumb component (is not aware of Redux store).
 *  - receives display items as props from parent component and composes child components dynamically (based on type) to output just the item list/grid/column part of UgcWall.
 *
 * Basic component composition:
 *
 *  - Collection:         </common/components/collections:[collection type]>      Displays wrapper elements for the collection.
 *    - Display items:      </common/components/displayItems:[display item type]>   Displays individual display items.
 * </pre>
 *
 * @docPreview {namedExport: "docPreviewExport"}
 *
 * @memberof module:UgcWall.Components.Elements
 * @namespace
 *
 * @return {ReactClass}
 */
class Items extends React.Component {
  /**
   * Constructor.
   *
   * @param {object} props The injected props
   */
  constructor(props) {
    super(props);

    // Bind methods.
    this.resize = this.resize.bind(this);
    this.getContainerClassNames = this.getContainerClassNames.bind(this);
    this.mapModelsToItems = this.mapModelsToItems.bind(this);
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Lifecycle methods */

  /**
   * Called when component mounts for the first time.  *
   * Dispatches resize action when appropriate.
   */
  componentDidMount() {
    // Dispatch resize action.
    this.resize();
  }

  /**
   * Called whenever component is re-rendered.
   * Dispatches resize action when appropriate.
   */
  componentDidUpdate() {
    // Dispatch resize action.
    this.resize();
  }

  /**
   * Called whenever component is about to be re-rendered.
   * Can be used for optimisation.
   */
  shouldComponentUpdate(nextProps, nextState) {
    return (
      nextProps.items !== this.props.items ||
      nextProps.isVisible !== this.props.isVisible ||
      nextProps.useExternalLinkHandler !== this.props.useExternalLinkHandler
    );
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Render methods */

  /**
   * Render the component. Compose child components as required.
   *
   * @return {module:UgcWall.Components.UgcWallItems}
   */
  render() {
    // Make sure item list is initialized.
    const _items = this.props.items ? this.props.items : [];

    // Generate a list of CSS classnames to append to the container Collection element, based on injected config.
    const _containerClassNames = this.getContainerClassNames(this.props.moduleOpts, this.props.templateOpts, _items);

    // Define the Collection component class to render.
    const LayoutComponent = ModuleUtils.getLayoutComponent(
      this.props.templateOpts.collectionType,
      this.props.moduleOpts.type
    );

    const { assetUrl, assetUgcUrl } = _.get(this.context, 'config', {});
    return (
      <If condition={this.props.isVisible}>
        <LayoutComponent
          ref="collectionContainer"
          className={_containerClassNames}
          animation={this.props.moduleOpts.animation}
          autoAdvance={this.props.moduleOpts.autoAdvance}
          template={this.props.moduleOpts.template}
          moduleId={this.props.id}
          layouts={this.props.moduleOpts.layouts}
          assetUrl={assetUrl}
          assetUgcUrl={assetUgcUrl}
          onItemClick={this.props.clickHandlers.viewItem}
          onLoadMore={this.props.pagingHandlers.showMore}
          currentPage={this.props.currentPage}
          totalPages={this.props.totalPages}
          showMoreMode={this.props.showMoreMode}
          moduleOpts={this.props.moduleOpts}
          newItems={this.mapModelsToItems(this.props.newItems, assetUrl, assetUgcUrl)}
        >
          {this.mapModelsToItems(_items, assetUrl, assetUgcUrl)}
        </LayoutComponent>
      </If>
    );
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Utility methods */

  // Build child items to inject into Collection component.
  mapModelsToItems(models, assetUrl, assetUgcUrl) {
    if (!_.isEmpty(models)) {
      return models.map((item, itemIndex) => {
        const customItems = this.props.customItems;
        const customItem = ItemUtils.getCustomItemForIndex(itemIndex, customItems);

        // If available, render custom item.
        if (customItem) {
          return (
            <Custom
              key={item.id}
              item={item}
              customItem={customItem}
              customItemIndex={itemIndex}
              moduleOpts={this.props.moduleOpts}
              templateOpts={this.props.templateOpts}
              itemOpts={this.props.itemOpts}
              assetUrl={assetUrl}
              assetUgcUrl={assetUgcUrl}
            />
          );

          // Otherwise, render standard item.
        } else {
          return itemFactory({
            id:                  item.id,
            item,
            moduleOpts:          this.props.moduleOpts,
            templateOpts:        this.props.templateOpts,
            itemOpts:            this.props.itemOpts,
            onClick:             this.props.clickHandlers.viewItem,
            assetUrl,
            assetUgcUrl,
            onExternalLinkClick: this.props.clickHandlers.externalLink,
          });
        }
      });
    }
  }

  /**
   * Waits for content to fully load, then dispatches resize API call (so parent frame can react accordingly).
   *
   */
  resize() {
    // Wait for images to finish loading, then resize.
    if (this.refs.collectionContainer) {
      imagesLoaded(this.refs.collectionContainer, () => {
        // Get the Events service.
        const eventService = NGXD.getServiceContainer().getService('event');

        // Raise event.
        eventService.trigger('collection:rendered');

        // Get Api service.
        const apiService = NGXD.getServiceContainer().getService('api');

        // Call resize method.
        apiService.execute('resizeParent');
      });
    }
  }

  /**
   * Utility method to generate css classnames for injection into the [Collection] component based on the module options.
   *
   * @param  {Object} moduleOpts   Module options.
   * @param  {Object} templateOpts Template options for the module.
   * @param  {Array}  items        Array of items.
   * @return {String} The css classnames.
   */
  getContainerClassNames(moduleOpts, templateOpts, items) {
    const classNames = ['xContainer'];

    classNames.push('xContainer' + UCFirst(moduleOpts.type));

    classNames.push('xTemplate' + capitalize(templateOpts.collectionType));
    classNames.push('xClass' + capitalize(templateOpts.collectionClass) + 'V2');
    classNames.push('xClass' + capitalize(templateOpts.collectionClass));
    classNames.push('xItemCount' + items.length);
    classNames.push('hasItem' + capitalize(templateOpts.itemTemplate.toLowerCase()));
    classNames.push('xRender' + capitalize(moduleOpts.render));
    classNames.push(`xShowMore${capitalize(moduleOpts.showMoreMode.replace(/mode.showmore./, ''))}`);

    if (templateOpts.variant) classNames.push('hasVariant' + capitalize(templateOpts.variant));

    return classNames.join(' ');
  }
}

/**
 * React PropTypes.
 *
 * @type {Object}
 * @property {String} id The module DOM id.
 * @property {Array<Common.Model.Entity.DisplayItem>} items Immutable array containing the DisplayItem objects.
 * @property {Array} customItems Array containg any custom items.
 * @property {Object} moduleOpts Object defining the various module options.
 * @property {Object} templateOpts Object defining the various module template options.
 * @property {Object} itemOpts Object defining the various item options.
 * @property {Object} clickHandlers Object defining clickhandlers for various events.
 * @property {Boolean} isVisible Boolean indicating if this UgcWalls parent state is currently active.
 */
Items.propTypes = {
  id:                     PropTypes.string,
  items:                  PropTypes.arrayOf(PropTypes.instanceOf(DisplayItem)),
  customItems:            PropTypes.object,
  moduleOpts:             PropTypes.object,
  templateOpts:           PropTypes.object,
  itemOpts:               PropTypes.object,
  clickHandlers:          PropTypes.object,
  isVisible:              PropTypes.bool,
  useExternalLinkHandler: PropTypes.bool,
  newItems:               PropTypes.arrayOf(PropTypes.instanceOf(DisplayItem)),
};

Items.contextTypes = {
  config: PropTypes.object,
};

/**
 * React default props.
 *
 * @type {Object}
 */
Items.defaultProps = {};

export default Items;
